import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Typography, Grid, Container, IconButton } from '@mui/material';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import ResetZoomIcon from '@mui/icons-material/CenterFocusStrong';

import { useNavigationBlocker } from '~/hooks/useNavigationBlocker';

// Actions
import { failure, success } from '~/redux/actions/snackbar-actions';
import { getProductDetails } from '~/redux/actions/template-builder';
import { SET_PRODUCT_DETAILS } from '~/redux/actions/action-types';

// Utils
import { COMPAIGNS_ACTION_TYPE, DEFAULT_PAGINATION_WITHOUT_PAGE_SIZE } from '~/utils/constants';
import request from '~/utils/request';
import { createEnvelopeProof, createViewProof, multiPageTemplates } from '~/utils/template-builder';
import { MESSAGES } from '~/utils/message';

// Hooks
import useCampaignDrafts from '~/hooks/useCampaignDrafts';

// Components
// import ActionDialog from './MailActionDialog/ActionDialog';
import ActionaDialogNew from './MailActionDialog/ActionaDialogNew';
// import DelayDialog from './DelayModal/DelayDialog';
import DelayDialogNew from './DelayModal/DelayDialogNew';
import SaveDialog from './SaveDialog/SaveDialog';
import WorkflowPanel from './WorkflowPanel/WorkflowPanel';
import AutomationSettingsPanel from './AutomationSettings/AutomationSettingsPanel';
import StepCard from './StepCard';
import ConfirmDialog from '../Account/Wallet/ConfirmDialog/ConfirmDialog';
import NameModal from './NameModal';
import DraftRecoveryAlert from './DraftRecoveryAlert';
import OlcButton from '~/components/General/OlcButton';

// Images
import {
  CancelOrderDetailsCIcon,
  OrderCancelCIcon,
  PlusIconCIcon,
  CampaignPencil,
  LeaveCampaignNew,
  DraftRecovery,
} from '~/assets/images';
import ConfirmModal from '../General/ConfirmModal';
import LimitModal from '../General/LimitModal';
import './styles.scss';

const CreateCampaign = () => {
  const { id: campaignId } = useParams();
  const isEditMode = !!campaignId;
  const [campaignName, setCampaignName] = useState('');
  const [description, setDescription] = useState('');
  const [steps, setSteps] = useState([]);
  const [loadingCampaign, setLoadingCampaign] = useState(false);
  const [openActionDialog, setOpenActionDialog] = useState(false);
  const [openDelayDialog, setOpenDelayDialog] = useState(false);
  const [openSaveModal, setOpenSaveModal] = useState(false);
  const [selectedActionType, setSelectedActionType] = useState('');
  const [delayConfig, setDelayConfig] = useState({ days: 1 });
  const [insertIndex, setInsertIndex] = useState(-1);
  const [editingStep, setEditingStep] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [openNameModal, setOpenNameModal] = useState(false);
  const location = useLocation();
  // Confirm Dialog states
  // const [openConfirmDialog, setOpenConfirmDialog] = useState(false);

  const [openConfirmModal, setOpenConfirmModal] = useState({
    type: '',
    open: false,
    message: '',
    handleOk: () => {},
    loading: false,
    header: '',
    successButtonName: '',
    icon: '',
    svg: '',
    className: '',
    modalName: '',
    handleClose: () => {},
  });

  // Mailer Details states
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateOptions, setTemplateOptions] = useState([]);
  const [selectedPostage, setSelectedPostage] = useState(null);
  const [selectedEnvelope, setSelectedEnvelope] = useState(null);
  const [offerPercentageValue, setOfferPercentageValue] = useState('');
  const [uvCoating, setUvCoating] = useState(false);

  // Previous selection states for back navigation
  const [previouslySelectedActionType, setPreviouslySelectedActionType] = useState('');
  const [previouslySelectedTemplate, setPreviouslySelectedTemplate] = useState(null);
  const [search, setSearch] = useState('');
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [limitModal, setLimitModal] = useState(false);
  const [limitMessage, setLimitMessage] = useState('');
  const [downloadingProof, setDownloadingProof] = useState({
    status: false,
    stepId: null,
  });
  const [downloadingEnvelope, setDownloadingEnvelope] = useState({
    status: false,
    stepId: null,
  });
  const [delayLoading, setDelayLoading] = useState(false);
  const [addingActionLoading, setAddingActionLoading] = useState(false);

  // Draft management state
  const [isDraftLoaded, setIsDraftLoaded] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const isSavingRef = React.useRef(false);
  const persistPage = React.useRef(true);
  const isEditingRef = React.useRef(false);

  // Initialize draft management
  const {
    hasDraft,
    draftData,
    isAutoSaving,
    showDraftAlert,
    saveDraft,
    clearDraft,
    handleContinueEditing,
    handleStartFresh,
    handleCancelDraftRecovery,
    handleFinalSave,
    setShowDraftAlert,
  } = useCampaignDrafts(campaignId, isEditMode);

  const products = useSelector((state) => state.productReducers.products);

  const deliveryOptions = useSelector(
    (state) => state.templateReducer.productDetailByTemplate.deliveryDuration
  );

  const deliveryTypes =
    useSelector((state) => state.templateReducer.productDetailByTemplate.deliveryType) || [];
  const detailsProductType = useSelector(
    (state) => state.templateReducer.productDetailByTemplate.productType
  );

  const postageOptions = useMemo(() => {
    const types = new Set(deliveryTypes);

    if (selectedTemplate?.productType === 'Postcards' && selectedTemplate?.postCardSize) {
      products
        ?.filter(
          (product) =>
            product.productType === selectedTemplate.productType &&
            product.paperSize === selectedTemplate.postCardSize
        )
        .forEach((product) => {
          if (product.deliveryType) types.add(product.deliveryType);
        });
    }

    return Array.from(types)
      .map((type, id) => {
        return {
          id,
          label: deliveryOptions?.[type] ? `${type} ${deliveryOptions[type]}` : type,
          value: type,
        };
      })
      .sort((a, b) => a?.value.localeCompare(b?.value))
      .sort((a, b) => {
        // First Class should always be first
        if (a.value === 'First Class' && b.value !== 'First Class') return -1;
        if (b.value === 'First Class' && a.value !== 'First Class') return 1;
        // Standard Class should be second (only if neither is First Class)
        if (
          a.value === 'Standard Class' &&
          b.value !== 'Standard Class' &&
          a.value !== 'First Class' &&
          b.value !== 'First Class'
        )
          return -1;
        if (
          b.value === 'Standard Class' &&
          a.value !== 'Standard Class' &&
          a.value !== 'First Class' &&
          b.value !== 'First Class'
        )
          return 1;
        // All other options maintain their order
        return 0;
      });
  }, [deliveryOptions, deliveryTypes, products, selectedTemplate]);

  const evelopeOptions =
    useSelector((state) => state.templateReducer.productDetailByTemplate.envelopeType)?.map(
      (types, id) => {
        return {
          id,
          label: types,
        };
      }
    ) || [];

  const templates = templateOptions
    .map((template) => {
      return {
        id: template.id,
        label: `ID:${template.id} ${template.title}`,
        productType: template.product.productType,
        productId: template.product.id,
        postageType: template.product.deliveryType,
        envelopeType: template.envelopeType,
        postCardSize: template.product.paperSize,
        frontThumbnailUrl: template.thumbnailUrl,
        backThumbnailUrl: template.backThumbnailUrl
          ? template.backThumbnailUrl
          : template.thumbnailUrl,
        templateType: template.templateType,
        meta: template.meta,
      };
    })
    .concat({
      id: '-1',
      label: 'Create New Template',
      custom: true,
      icon: PlusIconCIcon,
      func: () => window.open('/create-template', '_blank'),
    });

  const fetchTemplates = async (noSearch = false) => {
    try {
      setLoading(true);
      const productId = COMPAIGNS_ACTION_TYPE.find(
        (product) => product.type === selectedActionType
      )?.productId;
      const payload = {
        page: DEFAULT_PAGINATION_WITHOUT_PAGE_SIZE.page,
        pageSize: DEFAULT_PAGINATION_WITHOUT_PAGE_SIZE.pageSize,
        search: noSearch ? '' : search?.includes('ID:') ? search?.replace(/ID:\d+\s*/, '') : search,
        productIds: productId ? JSON.stringify([String(productId)]) : undefined,
      };
      const { data } = await request.get('templates', payload);
      setTemplateOptions(data?.data?.rows || []);
    } catch (err) {
      setTemplateOptions([]);
    } finally {
      setLoading(false);
    }
  };

  const getOneCampaign = async () => {
    try {
      setLoadingCampaign(true);
      const editedCampaign = await request.get(`automations/${campaignId}`);
      const campaign = editedCampaign.data?.data;
      setCampaignName(campaign?.name || '');
      setDescription(campaign?.description || '');
      setSteps(campaign?.config?.steps || []);

      // Mark as loaded for auto-save functionality in edit mode
      if (!isDraftLoaded) {
        setIsDraftLoaded(true);
      }
    } catch (error) {
      dispatch(failure('Failed to load campaign data.'));
      navigate('/campaigns');
      return error;
    } finally {
      setLoadingCampaign(false);
    }
  };

  const handleOfferPercentageChange = (e) => {
    const value = e.target.value;
    const regex = /^(100(\.0{1,2})?|[0-9]{1,2}(\.[0-9]{1,2})?)$/;
    if (!isNaN(value) && regex.test(value)) {
      setOfferPercentageValue(value);
    } else if (value === '' || value < 0 || value === 'e') {
      setOfferPercentageValue('');
    }
  };

  // Load campaign for edit mode or initialize create mode
  useEffect(() => {
    if (isEditMode && !isDraftLoaded) {
      getOneCampaign();
    } else if (!isEditMode && !hasDraft && !isDraftLoaded) {
      // Initialize create mode without existing draft
      setIsDraftLoaded(true);
    }
  }, [isEditMode, campaignId, isDraftLoaded, hasDraft]);

  useEffect(() => {
    if (selectedTemplate?.id) {
      // Keep the previous details while switching between templates of the same product type, so the
      // postage field doesn't unmount/remount. Clear them when the type changes to avoid stale options.
      if (detailsProductType !== selectedTemplate.productType) {
        dispatch({ type: SET_PRODUCT_DETAILS, payload: [] });
      }
      dispatch(getProductDetails({ templateId: selectedTemplate.id }));
      !editingStep && setOfferPercentageValue(selectedTemplate?.meta?.rosOfferPercentage);
    }

    if (!selectedTemplate) {
      dispatch({ type: SET_PRODUCT_DETAILS, payload: [] });
      fetchTemplates(true);
      setErrors({});
      setSelectedPostage(null);
      setSelectedEnvelope(null);
      setOfferPercentageValue('');
    }
  }, [selectedTemplate, selectedActionType]);

  useEffect(() => {
    if (!isEditingRef.current) {
      setSelectedTemplate(null);
    }
  }, [selectedActionType]);

  useEffect(() => {
    fetchTemplates();
  }, [selectedActionType, search]);

  useEffect(() => {
    if (selectedPostage === null && postageOptions.length === 1 && selectedTemplate) {
      setSelectedPostage(
        postageOptions.find((postage) => postage?.value === selectedTemplate?.postageType) ||
          postageOptions[0] ||
          null
      );
      setErrors((prev) => ({ ...prev, postage: false }));
    }
  }, [selectedPostage, selectedTemplate, postageOptions]);

  // Draft recovery handlers
  const onContinueEditing = () => {
    const draft = handleContinueEditing();
    setTimeout(() => {
      if (draft) {
        setCampaignName(draft.campaignName || '');
        setDescription(draft.description || '');
        setSteps(draft.steps || []);
        setIsDraftLoaded(true);
      }
    }, 500);
  };

  const onStartFresh = () => {
    handleStartFresh();
    setIsDraftLoaded(true);
  };

  const onCancelDraftRecovery = () => {
    const result = handleCancelDraftRecovery();
    if (result === 'cancel') {
      navigate('/campaigns');
    }
  };

  // Save on beforeunload (tab switch/close) to prevent data loss
  useEffect(() => {
    const handleBeforeUnload = () => {
      const shouldSave =
        (isDraftLoaded || (!isEditMode && !hasDraft) || (isEditMode && !loadingCampaign)) &&
        (campaignName.trim() || description.trim() || steps.length > 0);
      if (shouldSave && !isSavingRef.current) {
        console.log('Page unloading, saving draft immediately');
        // Use synchronous save for beforeunload
        const draftKey = isEditMode ? `edit-campaign-${campaignId}` : 'create-campaign';
        try {
          const cleanData = {
            campaignName: campaignName || '',
            description: description || '',
            steps: steps || [],
            lastModified: new Date().toISOString(),
          };
          localStorage.setItem(draftKey, JSON.stringify(cleanData));
          console.log('Draft saved on page unload');
        } catch (error) {
          console.error('Error saving on page unload:', error);
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [
    campaignName,
    description,
    steps,
    isDraftLoaded,
    isEditMode,
    hasDraft,
    loadingCampaign,
    campaignId,
  ]);

  // Zoom control functions
  const handleZoomIn = () => {
    setZoomLevel((prev) => Math.min(prev + 0.2, 2));
  };

  const handleZoomOut = () => {
    setZoomLevel((prev) => Math.max(prev - 0.2, 0.3));
  };

  const handleResetZoom = () => {
    setZoomLevel(1);
  };

  const handleAddAction = (index = steps.length) => {
    setOpenActionDialog(true);
    setInsertIndex(index);
  };

  const handleOpenDelayDialog = (index = steps.length) => {
    // Ensure delayConfig is set to default value 1 and editingStep is null when opening from left panel
    setDelayConfig({ days: 1 });
    setEditingStep(null);
    setInsertIndex(index);
    setOpenDelayDialog(true);
  };

  const validateAction = () => dispatch(failure('Please select a mail action to proceed'));

  const handleSaveAction = () => {
    if (addingActionLoading) return; // Prevent multiple clicks

    if (!selectedTemplate) {
      return setErrors((prev) => ({ ...prev, template: true }));
    }

    const isFixedPostage = postageOptions.length === 1;
    const isSelectedPostageValid =
      selectedPostage &&
      postageOptions.some((postage) => postage?.value === selectedPostage?.value);
    if (postageOptions.length === 0 || (!isFixedPostage && !isSelectedPostageValid)) {
      return setErrors((prev) => ({ ...prev, postage: true }));
    }

    if (!selectedEnvelope && !multiPageTemplates.includes(selectedTemplate?.productType)) {
      return setErrors((prev) => ({ ...prev, envelope: true }));
    }

    if (selectedTemplate?.meta?.rosEnabled && !offerPercentageValue) {
      return setErrors((prev) => ({ ...prev, offerPercentage: true }));
    }

    setAddingActionLoading(true);

    const selectedAction = COMPAIGNS_ACTION_TYPE.find(
      (action) => action.type === selectedActionType
    );
    const resolvedPostage = postageOptions.length === 1 ? postageOptions[0] : selectedPostage;
    const newStep = {
      id: editingStep ? editingStep.id : Date.now(),
      type: 'action',
      action: selectedActionType,
      templateId: selectedTemplate.id,
      productId:
        products.find(
          (product) =>
            product.productType === selectedTemplate?.productType &&
            product.deliveryType === (resolvedPostage?.value || selectedTemplate?.postageType) &&
            product.paperSize === selectedTemplate?.postCardSize &&
            (selectedEnvelope ? product.envelopeType === selectedEnvelope.label : true)
        )?.id ||
        COMPAIGNS_ACTION_TYPE.find((product) => product.type === selectedActionType)?.productId ||
        undefined,
      ...(offerPercentageValue && { rosOfferPercentage: offerPercentageValue }),
      uvCoating: !!uvCoating,
      label: selectedAction.label,
      description: selectedAction.description,
      color: selectedAction.color,
      meta: {
        selectedTemplate,
        selectedEnvelope,
        selectedPostage: resolvedPostage,
        ...(offerPercentageValue && {
          rosOfferPercentage: offerPercentageValue,
        }),
        uvCoating: !!uvCoating,
      },
    };

    const newSteps = [...steps];
    if (editingStep) {
      // Update existing step
      const stepIndex = steps.findIndex((step) => step.id === editingStep.id);
      newSteps[stepIndex] = newStep;
    } else {
      // Insert new step
      newSteps.splice(insertIndex, 0, newStep);
    }

    setSteps(newSteps);

    // Immediately save when step is added/updated
    setTimeout(() => {
      console.log('Step added/updated, saving immediately');
      saveDraft({
        campaignName,
        description,
        steps: newSteps,
      });
    }, 50);

    setOpenActionDialog(false);
    setTimeout(() => {
      setSelectedTemplate(null);
      setSelectedActionType('');
      setEditingStep(null);
      setAddingActionLoading(false);
      setUvCoating(false);
    }, 1);
  };

  const handleSaveDelay = () => {
    if (delayLoading) return; // Prevent multiple clicks

    setDelayLoading(true);

    const newStep = {
      id: editingStep ? editingStep.id : Date.now(),
      type: 'delay',
      delayInDays: delayConfig.days,
      label: `Delay ${delayConfig.days} ${delayConfig.days === 1 ? 'day' : 'days'}`,
      color: '#9e9e9e',
      config: { ...delayConfig },
    };
    const newSteps = [...steps];
    if (editingStep) {
      // Update existing step
      const stepIndex = steps.findIndex((step) => step.id === editingStep.id);
      newSteps[stepIndex] = newStep;
    } else {
      // Insert new step
      newSteps.splice(insertIndex, 0, newStep);
    }

    setSteps(newSteps);

    // Immediately save when delay is added/updated
    setTimeout(() => {
      console.log('Delay step added/updated, saving immediately');
      saveDraft({
        campaignName,
        description,
        steps: newSteps,
      });
    }, 50);

    setOpenDelayDialog(false);
    // Note: editingStep and delayConfig will be reset in onExited callback
    setTimeout(() => {
      setDelayLoading(false);
    }, 500);
  };

  const handleEditStep = (step) => {
    setEditingStep(step);
    if (step.type === 'action') {
      isEditingRef.current = true;
      setSelectedActionType(step.action);
      setSelectedTemplate(step.meta.selectedTemplate);
      setSelectedPostage(step.meta.selectedPostage);
      setSelectedEnvelope(step.meta.selectedEnvelope);
      setOfferPercentageValue(step?.meta?.rosOfferPercentage || '');
      setUvCoating(!!step?.meta?.uvCoating);
      setOpenActionDialog(true);
      setTimeout(() => {
        isEditingRef.current = false;
      }, 0);
    } else {
      setDelayConfig(step.config || { days: 1 });
      setOpenDelayDialog(true);
    }
  };

  const downloadViewProof = async (label, templateId, step = null) => {
    try {
      if (label && templateId) {
        setDownloadingProof({ status: true, stepId: step?.id || 'dialog' });
        await dispatch(createViewProof(label, templateId));
        setDownloadingProof({ status: false, stepId: null });
      }
    } catch (error) {
      setDownloadingProof({ status: false, stepId: null });
      dispatch(failure(MESSAGES.VIEW_PROOF_ERROR));
    }
  };

  const downloadEnvelopeProof = async (step, isStep = true) => {
    try {
      setDownloadingEnvelope({
        status: true,
        stepId: isStep ? step?.id : 'dialog',
      });
      let payload = null;
      const templateLabel = isStep ? step?.meta?.selectedTemplate.label : selectedTemplate.label;
      if (isStep) {
        payload = {
          productId:
            products.find(
              (product) =>
                product.productType === step?.meta?.selectedTemplate?.productType &&
                product.deliveryType === step?.meta?.selectedPostage?.value &&
                product.paperSize === step?.meta?.selectedTemplate?.postCardSize &&
                (step?.meta?.selectedEnvelope
                  ? product.envelopeType === step?.meta?.selectedEnvelope.label
                  : true)
            )?.id || step?.meta?.productId,
        };
      } else {
        payload = {
          productId:
            products.find(
              (product) =>
                product.productType === selectedTemplate?.productType &&
                product.deliveryType === selectedPostage?.value &&
                product.paperSize === selectedTemplate?.postCardSize &&
                (selectedEnvelope ? product.envelopeType === selectedEnvelope.label : true)
            )?.id || selectedTemplate.productId,
        };
      }
      await dispatch(createEnvelopeProof(templateLabel, payload));
      setDownloadingEnvelope({ status: false, stepId: null });
    } catch (error) {
      console.log(error);
      setDownloadingEnvelope({ status: false, stepId: null });
      dispatch(failure(MESSAGES.VIEW_PROOF_ERROR));
    }
  };

  const handleDeleteStep = (index) => {
    const stepToDelete = steps[index];
    const newSteps = [...steps];

    // If deleting a delay, check if it's between two actions
    if (stepToDelete.type === 'delay') {
      const prevStep = steps[index - 1];
      const nextStep = steps[index + 1];

      // If delay is between two actions, prevent deletion
      if (prevStep && prevStep.type === 'action' && nextStep && nextStep.type === 'action') {
        dispatch(
          failure(
            'You cannot delete a delay that is between two actions. There must be a delay between consecutive actions in your workflow.'
          )
        );
        return;
      }

      // Otherwise, allow deletion of the delay
      newSteps.splice(index, 1);
    }
    // If deleting an action, also delete the delay below it (if it exists)
    else if (stepToDelete.type === 'action') {
      const nextStep = steps[index + 1];
      if (nextStep && nextStep.type === 'delay') {
        // Delete both the action and the delay below it
        // Delete from higher index first to avoid array shifting issues
        newSteps.splice(index + 1, 1); // Remove delay first
        newSteps.splice(index, 1); // Then remove action
      } else {
        // Just delete the action
        newSteps.splice(index, 1);
      }
    }

    setSteps(newSteps);

    // Immediately save when step is deleted
    if (newSteps.length > 0) {
      setTimeout(() => {
        console.log('Step deleted, saving immediately');
        saveDraft({
          campaignName,
          description,
          steps: newSteps,
        });
      }, 50);
    } else if (newSteps.length === 0) {
      clearDraft();
    }
  };

  const handleSaveAutomationClick = () => {
    // Check if there's at least one action
    if (!hasAtLeastOneAction()) {
      dispatch(failure('Please add at least one action to your campaign.'));
      return;
    }

    // Check if last step is an action
    const lastStep = steps[steps.length - 1];
    if (lastStep && lastStep.type !== 'action') {
      dispatch(
        failure(
          'Your campaign must end with an action, not a delay. Please add a final action or remove the last delay.'
        )
      );
      return;
    }

    // Open modal to ask for automation name
    setOpenSaveModal(true);
  };

  const handleCancelCampaign = () => {
    const hasContent = campaignName.trim() || description.trim() || steps.length > 0;

    if (!hasContent) {
      // Clean up any existing draft and navigate
      clearDraft();
      navigate('/campaigns');
    } else {
      setOpenConfirmModal({
        type: 'cancel',
        open: true,
        header: 'Cancel Campaign',
        successButtonName: 'Yes',
        icon: CancelOrderDetailsCIcon,
        compactActions: true,
        modalName: 'cancel-campaign-modal',
        message: 'Are you sure you want to cancel?',
        description: 'All unsaved changes will be lost.',
        handleOk: () => {
          persistPage.current = false;
          clearDraft();
          navigate('/campaigns');
        },
        handleClose: () => {
          setOpenConfirmModal((prev) => ({
            ...prev,
            open: false,
          }));
        },
      });
    }
  };

  const handleSaveAutomation = async (values) => {
    isSavingRef.current = true;
    try {
      setSaveLoading(true);

      // Update local state for campaignName/description for consistency
      setCampaignName(values.campaignName);
      setDescription(values.description);

      const payload = {
        name: values.campaignName,
        description: values.description,
        config: {
          steps,
        },
      };

      const responnse = isEditMode
        ? await request.put(`automations/${campaignId}`, payload)
        : await request.post('automations', payload);

      // Clean up draft on successful save
      handleFinalSave();

      setOpenSaveModal(false);
      dispatch(success(responnse.data.message || 'Campaign created successfully'));
      setTimeout(() => {
        clearDraft();
      }, 500);
      persistPage.current = false;
      navigate('/campaigns');
    } catch (error) {
      if (error?.response?.status === 418) {
        setOpenSaveModal(false);
        setLimitMessage(error?.response?.data?.message || '');
        setLimitModal(true);
      } else {
        dispatch(failure(error?.response?.data?.message || 'Failed to save campaign.'));
      }
      return error;
    } finally {
      isSavingRef.current = false;
      setTimeout(() => {
        setSaveLoading(false);
      }, 1000);
    }
  };

  // Helper function to check if automation has at least one action
  const hasAtLeastOneAction = () => {
    return steps.some((step) => step.type === 'action');
  };

  const getActionTypeDetails = (type) => {
    return COMPAIGNS_ACTION_TYPE.find((action) => action.type === type);
  };

  // Helper function to check if we can add actions from sidebar
  const canAddActionFromSidebar = () => {
    if (steps.length === 0) return true; // First step is always an action
    const lastStep = steps[steps.length - 1];
    return lastStep.type === 'delay'; // Can only add action after delay
  };

  const handleUpdateCampaignName = (values) => {
    const newName = values.campaignName;
    setCampaignName(newName);
    setTimeout(() => {
      console.log('Campaign name updated, saving immediately');
      saveDraft({
        campaignName: newName,
        description,
        steps,
      });
    }, 50);
    setOpenNameModal(false);
  };

  // Block navigation if there are unsaved steps
  useNavigationBlocker(
    () => steps.length > 0 && persistPage.current === true,
    (nextUrl) => {
      setOpenConfirmModal({
        type: 'leave-campaign-creation',
        open: true,
        header: 'Leave Campaign Creation?',
        successButtonName: 'Stay on Page',
        icon: LeaveCampaignNew,
        compactActions: true,
        modalName: 'leave-campaign-modal',
        message: 'You have unsaved changes. If you leave this page now, your changes will be lost.',
        handleOk: () => setOpenConfirmModal((prev) => ({ ...prev, open: false })),
        handleClose: () => {
          clearDraft();
          window.location.href = nextUrl?.pathname || '/campaigns';
        },
      });
    }
  );

  return (
    <Container
      maxWidth="xxl"
      className="campaignBuilderPage"
      sx={{
        padding: '0 24px 24px !important',
        margin: '0 auto',
        width: '100%',
        maxWidth: 'none !important',
        '@media (max-width: 600px)': {
          padding: '0 12px 16px !important',
        },
      }}
    >
      <Box className="campaignBuilderShell">
        <AutomationSettingsPanel
          COMPAIGNS_ACTION_TYPE={COMPAIGNS_ACTION_TYPE}
          canAddActionFromSidebar={canAddActionFromSidebar}
          setSelectedActionType={setSelectedActionType}
          setOpenActionDialog={setOpenActionDialog}
          setInsertIndex={setInsertIndex}
          setOpenDelayDialog={handleOpenDelayDialog}
          steps={steps}
        />

        {/* Right Panel - Workflow Builder */}
        <Box
          className="campaignBuilderWorkspace"
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            '@media (max-width: 1024px)': {
              marginLeft: 0,
              width: '100%',
            },
          }}
        >
          {/* Header Bar */}
          <Box
            className="campaignBuilderTopBar"
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              backgroundColor: '#fff',
              // borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
              minHeight: '64px',
            }}
          >
            {/* Left side - Campaign Name */}
            <Box
              className="campaignNameControl"
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <Typography className="campaignNameText">
                {campaignName || 'Untitled Campaign'}
              </Typography>
              <IconButton
                aria-label="Edit campaign name"
                onClick={() => setOpenNameModal(true)}
                sx={{
                  padding: '4px',
                  '&:hover': {
                    backgroundColor: 'transparent',
                  },
                }}
              >
                <img src={CampaignPencil} alt="" style={{ width: '24px', height: '24px' }} />
              </IconButton>
            </Box>

            {/* Right side - Buttons and Zoom Controls */}
            <Box className="campaignsButtons">
              <OlcButton variant="secondary" size="small" onClick={handleCancelCampaign}>
                Cancel
              </OlcButton>
              <OlcButton variant="primary" size="small" onClick={handleSaveAutomationClick}>
                {isEditMode ? 'Update' : 'Save'}
              </OlcButton>
            </Box>
          </Box>

          {/* Workflow Content */}
          <WorkflowPanel
            steps={steps}
            zoomLevel={zoomLevel}
            handleZoomIn={handleZoomIn}
            handleZoomOut={handleZoomOut}
            handleResetZoom={handleResetZoom}
            StepCard={StepCard}
            handleAddAction={handleAddAction}
            handleEditStep={handleEditStep}
            handleDeleteStep={handleDeleteStep}
            handleDownloadMailerProof={downloadViewProof}
            downloadingProof={downloadingProof}
            handleDownloadEnvelopeProof={downloadEnvelopeProof}
            downloadingEnvelope={downloadingEnvelope}
            setEditingStep={setEditingStep}
            setInsertIndex={setInsertIndex}
            setOpenDelayDialog={handleOpenDelayDialog}
            setOpenActionDialog={setOpenActionDialog}
            loadingCampaign={loadingCampaign}
            // hideZoomControls={true}
          />
        </Box>
      </Box>

      {/* Action Dialog */}
      {/* <ActionDialog
        open={openActionDialog}
        onClose={(event, reason) => {
          if (reason !== 'backdropClick') {
            setOpenActionDialog(false);
            setTimeout(() => {
              setEditingStep(null);
              setSelectedTemplate(null);
              setSelectedActionType('');
              // Clear previous selections when dialog is fully closed
              setPreviouslySelectedActionType('');
              setPreviouslySelectedTemplate(null);
            }, 1);
          }
        }}
        selectedActionType={selectedActionType}
        setSelectedActionType={setSelectedActionType}
        editingStep={editingStep}
        handleSaveAction={handleSaveAction}
        addingActionLoading={addingActionLoading}
        getActionTypeDetails={getActionTypeDetails}
        COMPAIGNS_ACTION_TYPE={COMPAIGNS_ACTION_TYPE}
        templates={templates}
        setSelectedTemplate={setSelectedTemplate}
        selectedTemplate={selectedTemplate}
        setSearch={setSearch}
        setErrors={setErrors}
        errors={errors}
        loading={loading}
        postageOptions={postageOptions}
        setSelectedPostage={setSelectedPostage}
        selectedPostage={selectedPostage}
        evelopeOptions={evelopeOptions}
        setSelectedEnvelope={setSelectedEnvelope}
        selectedEnvelope={selectedEnvelope}
        offerPercentageValue={offerPercentageValue}
        handleOfferPercentageChange={handleOfferPercentageChange}
        handleDownloadMailerProof={downloadViewProof}
        downloadingProof={downloadingProof}
        handleDownloadEnvelopeProof={downloadEnvelopeProof}
        downloadingEnvelope={downloadingEnvelope}
        validateAction={validateAction}
        previouslySelectedActionType={previouslySelectedActionType}
        setPreviouslySelectedActionType={setPreviouslySelectedActionType}
        previouslySelectedTemplate={previouslySelectedTemplate}
        setPreviouslySelectedTemplate={setPreviouslySelectedTemplate}
      />  */}

      <ActionaDialogNew
        open={openActionDialog}
        onClose={(event, reason) => {
          if (reason !== 'backdropClick') {
            setOpenActionDialog(false);
            setTimeout(() => {
              setEditingStep(null);
              setSelectedTemplate(null);
              setSelectedActionType('');
              setPreviouslySelectedActionType('');
              setPreviouslySelectedTemplate(null);
              setUvCoating(false);
            }, 200);
          }
        }}
        selectedActionType={selectedActionType}
        setSelectedActionType={setSelectedActionType}
        editingStep={editingStep}
        handleSaveAction={handleSaveAction}
        addingActionLoading={addingActionLoading}
        getActionTypeDetails={getActionTypeDetails}
        COMPAIGNS_ACTION_TYPE={COMPAIGNS_ACTION_TYPE}
        templates={templates}
        setSelectedTemplate={setSelectedTemplate}
        selectedTemplate={selectedTemplate}
        setSearch={setSearch}
        setErrors={setErrors}
        errors={errors}
        loading={loading}
        postageOptions={postageOptions}
        setSelectedPostage={setSelectedPostage}
        selectedPostage={selectedPostage}
        evelopeOptions={evelopeOptions}
        setSelectedEnvelope={setSelectedEnvelope}
        selectedEnvelope={selectedEnvelope}
        offerPercentageValue={offerPercentageValue}
        handleOfferPercentageChange={handleOfferPercentageChange}
        handleDownloadMailerProof={downloadViewProof}
        downloadingProof={downloadingProof}
        handleDownloadEnvelopeProof={downloadEnvelopeProof}
        downloadingEnvelope={downloadingEnvelope}
        validateAction={validateAction}
        previouslySelectedActionType={previouslySelectedActionType}
        setPreviouslySelectedActionType={setPreviouslySelectedActionType}
        previouslySelectedTemplate={previouslySelectedTemplate}
        setPreviouslySelectedTemplate={setPreviouslySelectedTemplate}
        setOfferPercentageValue={setOfferPercentageValue}
        uvCoating={uvCoating}
        setUvCoating={setUvCoating}
      />

      {/* Delay Dialog */}
      <DelayDialogNew
        open={openDelayDialog}
        onClose={() => {
          setOpenDelayDialog(false);
        }}
        onExited={() => {
          setEditingStep(null);
          setDelayConfig({ days: 1 });
        }}
        editingStep={editingStep}
        delayConfig={delayConfig}
        setDelayConfig={setDelayConfig}
        handleSaveDelay={handleSaveDelay}
        delayLoading={delayLoading}
      />

      {/* Save Modal */}
      <SaveDialog
        open={openSaveModal}
        onClose={() => setOpenSaveModal(false)}
        campaignName={campaignName}
        description={description}
        handleSaveAutomation={handleSaveAutomation}
        isLoading={saveLoading}
        isEditMode={isEditMode}
      />

      {/* Confirm Dialog */}
      {/* <ConfirmDialog
        open={openConfirmDialog}
        handleClose={() => {
          setOpenConfirmDialog(false);
        }}
        header={MESSAGES.COMPAIGNS.CANCEL_CAMPAIGN_MODAL.HEADING_NEW}
        successButtonName={MESSAGES.COMPAIGNS.CANCEL_CAMPAIGN_MODAL.SUBMIT_BUTTON}
        message={
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              marginTop: '-10px',
            }}
          >
            <Typography>{MESSAGES.COMPAIGNS.CANCEL_CAMPAIGN_MODAL.DESCRIPTION_NEW}</Typography>
            <Typography
              sx={{
                fontWeight: '400 !important',
              }}
            >
              {MESSAGES.COMPAIGNS.CANCEL_CAMPAIGN_MODAL.DESCRIPTION_SECONDARY}
            </Typography>
          </Box>
        }
        handleOk={() => {
          navigate('/campaigns');
        }}
        cancelButtonName={MESSAGES.COMPAIGNS.CANCEL_CAMPAIGN_MODAL.CANCEL_BUTTON}
        className="confirmCampaignModal"
        campaignModal={true}
        icon={<img src={OrderCancelCIcon} alt="icon" />}
      /> */}
      <ConfirmModal openConfirmModal={openConfirmModal} />

      <LimitModal
        open={limitModal}
        onClose={() => {
          setLimitModal(false);
          setTimeout(() => setLimitMessage(''), 100);
        }}
        message={limitMessage}
        resourceType="campaigns"
      />
      <NameModal
        open={openNameModal}
        onClose={() => setOpenNameModal(false)}
        onSubmit={handleUpdateCampaignName}
        initialName={campaignName}
        loading={saveLoading}
      />

      {/* Draft Recovery Alert */}
      <DraftRecoveryAlert
        open={showDraftAlert}
        onClose={(event, reason) => {
          if (reason !== 'backdropClick') {
            setShowDraftAlert(false);
          }
        }}
        onContinue={onContinueEditing}
        onStartFresh={onStartFresh}
        onCancel={onCancelDraftRecovery}
        isAutoSaving={isAutoSaving}
        loadingCampaign={loadingCampaign}
        isEditMode={isEditMode}
        title={
          isEditMode
            ? MESSAGES.COMPAIGNS.DRAFT_RECOVERY.EDIT.TITLE
            : MESSAGES.COMPAIGNS.DRAFT_RECOVERY.CREATE.TITLE
        }
        description={
          isEditMode
            ? MESSAGES.COMPAIGNS.DRAFT_RECOVERY.EDIT.DESCRIPTION
            : MESSAGES.COMPAIGNS.DRAFT_RECOVERY.CREATE.DESCRIPTION
        }
        message={
          isEditMode
            ? MESSAGES.COMPAIGNS.DRAFT_RECOVERY.EDIT.MESSAGE
            : MESSAGES.COMPAIGNS.DRAFT_RECOVERY.CREATE.MESSAGE
        }
        draftData={draftData}
      />
    </Container>
  );
};

export default CreateCampaign;
